import mustache from "mustache";
import { AnalisisLaboratorioResponse } from "../../../../modulos/fertilizacion/response/AnalisisLaboratorioResponse";
import { AnalisisRegistroResponse } from "../../../../modulos/fertilizacion/response/AnalisisRegistroResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  severidadColor,
  severidadLabel,
} from "../../../../utils/fertilizacionUtils";
import { _2d } from "../../../../utils/numberUtils";
import { IConnectRender } from "../../../definitions/IConnectRender";
import { FertilizacionPlanCompraModalRender } from "../modal/FertilizacionPlanCompraModalRender";
import {
  DiagnosticoCard,
  FertilizacionDiagnosticoCardRender,
} from "./FertilizacionDiagnosticoCardRender";
import template from "./fertilizacionDiagnosticoTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let FERTI_DIAG_ID = 0;

/** Severidades agregadas que manda el server en `registro.severityLevel`. */
const SEVERIDAD_CRITICA = "critical";
const SEVERIDAD_DEFICIENTE = "deficient";
const SEVERIDAD_MANTENIMIENTO = "maintenance";

/** Cuerpo del diagnóstico de un análisis (Render/Connect, una sola columna):
 *  fila de KPIs derivados de la severidad por lote y, debajo, el detalle por
 *  grupo (modo Nitrofert) o por lote (modo Sextante, donde `grupos` viene
 *  vacío). Es solo lectura: no toca el server. */
export class FertilizacionDiagnosticoRender implements IConnectRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _id: number;

  /**
   * @param _analisis Análisis ya diagnosticado (con recomendación generada).
   * @param _lotes    Lotes de la finca (nombre/cultivo/área de cada registro).
   */
  constructor(
    private readonly _analisis: AnalisisLaboratorioResponse,
    private readonly _lotes: ClienteFincaLoteResponse[],
  ) {
    FERTI_DIAG_ID += 1;
    this._id = FERTI_DIAG_ID;
  }

  render(): string {
    const porGrupo = this._analisis.grupos.length > 0;
    return mustache.render(template, {
      id: this._id,
      lotes: this._buildLotes(),
      kpis: this._buildKpis(),
      seccionTitulo: porGrupo ? "Diagnóstico por grupo" : "Diagnóstico por lote",
      seccionSub: "Química pura · sin productos",
      cards: this._buildCards(porGrupo)
        .map((card) => new FertilizacionDiagnosticoCardRender(card).render())
        .join(""),
    });
  }

  /** Las cards del análisis: una por grupo cuando el server los mandó (modo
   *  Nitrofert), una por registro con lote resuelto cuando no (modo Sextante).
   *  Arrancan desplegadas salvo las que están solo en mantenimiento. */
  private _buildCards(porGrupo: boolean): DiagnosticoCard[] {
    const cards = porGrupo
      ? this._buildCardsPorGrupo()
      : this._buildCardsPorLote();
    return cards.sort((a, b) => this._orden(a.registro) - this._orden(b.registro));
  }

  /** Orden de las cards: primero las enmiendas críticas — las que tienen una
   *  condición estructural que compromete la absorción (`efficiencyWarning`) —
   *  y después por severidad. Se mira el bloqueo aparte de `severityLevel` para
   *  que la enmienda quede primera aunque el server clasifique distinto. */
  private _orden(registro: AnalisisRegistroResponse): number {
    const bloqueado = registro.nutrientes.some(
      (nutriente) => nutriente.efficiencyWarning != null,
    );
    return (bloqueado ? 0 : 10) + this._severidadOrden(registro.severityLevel);
  }

  /** Peso de la severidad para ordenar (menor = más urgente). */
  private _severidadOrden(severityLevel: string | null): number {
    switch (severityLevel) {
      case SEVERIDAD_CRITICA:
        return 0;
      case SEVERIDAD_DEFICIENTE:
        return 1;
      case SEVERIDAD_MANTENIMIENTO:
        return 2;
      default:
        return 3;
    }
  }

  /** Una card por grupo: los lotes salen de los registros con ese `grupoId` y
   *  el plan del registro marcado `esReferencia` (o el primero del grupo). */
  private _buildCardsPorGrupo(): DiagnosticoCard[] {
    const loteById = new Map(this._lotes.map((lote) => [lote.id, lote]));
    return this._analisis.grupos
      .map((grupo, index) => {
        const registros = this._analisis.registros.filter(
          (registro) => registro.grupoId === grupo.id,
        );
        const referencia =
          registros.find((registro) => registro.esReferencia) ?? registros[0];
        if (!referencia) return null;
        const lotes = registros
          .map((registro) =>
            registro.loteId ? loteById.get(registro.loteId) : undefined,
          )
          .filter((lote) => lote !== undefined);
        return {
          registro: referencia,
          lotes,
          tag:
            registros.length > 1
              ? `${registros.length} lotes agrupados`
              : "Lote único",
          esGrupo: registros.length > 1,
          areaHa: grupo.totalAreaHa,
          cardId: `ferti-card-${this._id}-${index}`,
          open: referencia.severityLevel !== SEVERIDAD_MANTENIMIENTO,
        };
      })
      .filter((card) => card !== null);
  }

  /** Una card por registro con lote resuelto (modo Sextante: sin grupos). */
  private _buildCardsPorLote(): DiagnosticoCard[] {
    const loteById = new Map(this._lotes.map((lote) => [lote.id, lote]));
    return this._analisis.registros
      .map((registro, index) => {
        const lote = registro.loteId
          ? loteById.get(registro.loteId)
          : undefined;
        if (!lote) return null;
        return {
          registro,
          lotes: [lote],
          tag: "Lote único",
          esGrupo: false,
          areaHa: registro.planAreaHa ?? lote.areaHectareas,
          cardId: `ferti-card-${this._id}-${index}`,
          open: registro.severityLevel !== SEVERIDAD_MANTENIMIENTO,
        };
      })
      .filter((card) => card !== null);
  }

  connect(): void {
    this._planBtn = document.getElementById(`ferti-diag-plan-${this._id}`);
    this._planBtn?.addEventListener("click", this._onPlanClick);
  }

  dispose(): void {
    this._planBtn?.removeEventListener("click", this._onPlanClick);
    this._planBtn = null;
  }

  /** Botón del footer que abre el plan de compra. */
  private _planBtn: HTMLElement | null = null;

  /** Abre el plan de compra **encima** de este modal: al cerrarlo, el usuario
   *  vuelve al diagnóstico sin navegación propia. */
  private _onPlanClick = (): void => {
    new FertilizacionPlanCompraModalRender(this._analisis, this._lotes).init();
  };

  /** Lista de lotes diagnosticados que encabeza la pantalla: dot de severidad +
   *  nombre + "cultivo · área". El `title` lleva el resumen del plan cuando el
   *  server lo mandó. Solo los registros con lote resuelto. */
  private _buildLotes(): {
    nombre: string;
    sub: string;
    color: string;
    title: string;
  }[] {
    const loteById = new Map(this._lotes.map((lote) => [lote.id, lote]));
    return this._analisis.registros
      .map((registro) => {
        const lote = registro.loteId
          ? loteById.get(registro.loteId)
          : undefined;
        if (!lote) return null;
        return {
          nombre: lote.nombreLote,
          sub: `${lote.cultivo?.nombre ?? "Sin especificar"} · ${_2d(lote.areaHectareas)} ha`,
          color: severidadColor(registro.severityLevel),
          title:
            registro.planSummary ?? severidadLabel(registro.severityLevel),
        };
      })
      .filter((row) => row !== null);
  }

  /** Los 4 KPIs del encabezado, contando los registros por `severityLevel`.
   *  Solo cuentan los registros con lote resuelto: son los únicos que el
   *  server diagnostica. */
  private _buildKpis(): {
    tono: string;
    label: string;
    valor: number;
    sub: string;
  }[] {
    const registros = this._analisis.registros.filter(
      (registro) => registro.loteId !== null,
    );
    const contar = (severidad: string): number =>
      registros.filter((registro) => registro.severityLevel === severidad)
        .length;

    const grupos = this._analisis.grupos.length;
    return [
      {
        tono: "all",
        label: "Lotes analizados",
        valor: registros.length,
        sub: grupos > 0 ? `${grupos} grupos identificados` : this._subLotes(),
      },
      {
        tono: "red",
        label: "Enmienda urgente",
        valor: contar(SEVERIDAD_CRITICA),
        sub: "NPK bloqueado hasta corregir",
      },
      {
        tono: "amber",
        label: "Con deficiencias",
        valor: contar(SEVERIDAD_DEFICIENTE),
        sub: "Corrección esta temporada",
      },
      {
        tono: "green",
        label: "Sin problemas",
        valor: contar(SEVERIDAD_MANTENIMIENTO),
        sub: "Solo mantenimiento",
      },
    ];
  }

  /** Subtítulo del primer KPI cuando no hay grupos: área total diagnosticada. */
  private _subLotes(): string {
    const loteById = new Map(this._lotes.map((lote) => [lote.id, lote]));
    const area = this._analisis.registros.reduce((total, registro) => {
      const lote = registro.loteId ? loteById.get(registro.loteId) : undefined;
      return total + (lote?.areaHectareas ?? 0);
    }, 0);
    return `${_2d(area)} ha diagnosticadas`;
  }
}
